import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  Box, Typography, Button, CircularProgress, Alert, Snackbar
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import AddIcon from '@mui/icons-material/Add'
import RefreshIcon from '@mui/icons-material/Refresh'
import EligibleItemsTable from '../components/dc/EligibleItemsTable'
import { dcApi } from '../services/dc.api'

export default function DCEligibleItemsPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(50)
  const [search, setSearch] = useState('')
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success'
  })

  const { data, isLoading, error } = useQuery({
    queryKey: ['dc-eligible-items', page, limit, search],
    queryFn: () => dcApi.getEligibleItems({ page, limit, search: search || undefined })
  })

  const addMutation = useMutation({
    mutationFn: (medicineIds: number[]) => dcApi.addManagedItems(medicineIds),
    onSuccess: (result) => {
      setSnackbar({ open: true, message: `${result.length} items added to DC management`, severity: 'success' })
      setSelectedIds([])
      queryClient.invalidateQueries({ queryKey: ['dc-eligible-items'] })
      queryClient.invalidateQueries({ queryKey: ['dc-managed-items'] })
    },
    onError: (err: Error) => {
      setSnackbar({ open: true, message: err.message, severity: 'error' })
    }
  })

  const handleAddSelected = () => {
    if (selectedIds.length > 0) {
      addMutation.mutate(selectedIds)
    }
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return <Alert severity="error">Failed to load eligible items: {(error as Error).message}</Alert>
  }

  const items = data?.items || []
  const total = data?.pagination?.total || 0

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/orders')}>
            Back
          </Button>
          <Typography variant="h5">Eligible Items for DC</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
            ({total} items meet criteria)
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => queryClient.invalidateQueries({ queryKey: ['dc-eligible-items'] })}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddSelected}
            disabled={selectedIds.length === 0 || addMutation.isPending}
          >
            {addMutation.isPending ? 'Adding...' : `Add ${selectedIds.length} to DC`}
          </Button>
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Items with 3+ distinct bills every month for past 6 months in any store, and available from DC distributors. Sorted by monthly savings.
      </Alert>

      <EligibleItemsTable
        items={items}
        total={total}
        page={page}
        limit={limit}
        search={search}
        onSearchChange={(s) => { setSearch(s); setPage(1) }}
        onPageChange={setPage}
        onLimitChange={(l) => { setLimit(l); setPage(1) }}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        message={snackbar.message}
      />
    </Box>
  )
}
